<template>
  <div class="tg-widget">
    <!-- Floating button -->
    <a
      :href="telegramUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="tg-fab"
      :class="{ 'tg-fab-expanded': expanded }"
      @mouseenter="expanded = true"
      @mouseleave="expanded = false"
      :title="T.tooltip"
    >
      <svg class="tg-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="12" fill="#229ED9"/>
        <path d="M5.16 11.55l12.1-4.67c.56-.2 1.05.14.87.95l-2.07 9.73c-.15.68-.55.84-1.12.53l-3.03-2.23-1.46 1.4c-.16.16-.3.3-.62.3l.22-3.1 5.64-5.1c.25-.22-.05-.34-.38-.13L7.45 14.3l-2.95-.92c-.64-.2-.65-.64.34-1z" fill="#fff"/>
      </svg>
      <span class="tg-label">{{ T.label }}</span>
    </a>

    <!-- Tooltip balloon (pops on first visit) -->
    <transition name="tg-balloon">
      <div v-if="showBalloon" class="tg-balloon">
        <button class="tg-balloon-close" @click="closeBalloon">✕</button>
        <div class="tg-balloon-icon">💬</div>
        <div class="tg-balloon-text">{{ T.balloon }}</div>
        <a :href="telegramUrl" target="_blank" class="tg-balloon-btn" @click="closeBalloon">
          {{ T.balloonBtn }}
        </a>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useLocaleStore } from '../stores/locale';

const TELEGRAM_BOT = 'MobiFIXPRO_bot';

export default {
  name: 'TelegramWidget',
  setup() {
    const locale = useLocaleStore();
    const expanded = ref(false);
    const showBalloon = ref(false);

    const T = computed(() => {
      const uk = {
        label: 'Написати майстру',
        tooltip: 'Зв\'язатися з майстром у Telegram',
        balloon: 'Є питання? Напишіть нам у Telegram — відповімо швидко!',
        balloonBtn: 'Відкрити Telegram'
      };
      const en = {
        label: 'Chat with master',
        tooltip: 'Contact our master in Telegram',
        balloon: 'Any questions? Write us in Telegram — we reply fast!',
        balloonBtn: 'Open Telegram'
      };
      return locale.lang === 'uk' ? uk : en;
    });

    const telegramUrl = computed(() => {
      const text = locale.lang === 'uk'
        ? 'Привіт! Хочу дізнатися про ремонт телефону.'
        : 'Hello! I\'d like to inquire about phone repair.';
      return `https://t.me/${TELEGRAM_BOT}?text=${encodeURIComponent(text)}`;
    });

    function closeBalloon() {
      showBalloon.value = false;
      try { localStorage.setItem('tg_balloon_shown', '1'); } catch {}
    }

    onMounted(() => {
      let shown = false;
      try { shown = !!localStorage.getItem('tg_balloon_shown'); } catch {}
      if (!shown) {
        setTimeout(() => { showBalloon.value = true; }, 3500);
      }
    });

    return { expanded, showBalloon, T, telegramUrl, closeBalloon };
  }
};
</script>

<style scoped>
.tg-widget {
  position: fixed;
  bottom: 28px;
  right: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}

/* FAB button */
.tg-fab {
  display: flex;
  align-items: center;
  gap: 0;
  background: #229ED9;
  color: #fff;
  border-radius: 50px 0 0 50px;
  text-decoration: none;
  box-shadow: -2px 4px 20px rgba(34,158,217,0.45);
  overflow: hidden;
  transition: box-shadow 0.25s, gap 0.25s;
  max-width: 58px;
  padding: 0;
  cursor: pointer;
}
.tg-fab:hover, .tg-fab-expanded {
  max-width: 220px;
  gap: 10px;
  box-shadow: -2px 6px 28px rgba(34,158,217,0.6);
}

.tg-icon {
  width: 58px;
  height: 58px;
  flex-shrink: 0;
  border-radius: 50%;
}

.tg-label {
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  padding-right: 18px;
  opacity: 0;
  max-width: 0;
  overflow: hidden;
  transition: opacity 0.2s 0.05s, max-width 0.25s;
}
.tg-fab-expanded .tg-label {
  opacity: 1;
  max-width: 160px;
}

/* Balloon */
.tg-balloon {
  background: #fff;
  border: 1px solid #e0dff8;
  border-radius: 18px;
  padding: 16px 18px;
  width: 240px;
  box-shadow: 0 8px 32px rgba(83,74,183,0.18);
  position: relative;
}
.tg-balloon::after {
  content: '';
  position: absolute;
  bottom: -10px;
  right: 24px;
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-top: 10px solid #fff;
  filter: drop-shadow(0 2px 2px rgba(0,0,0,0.06));
}
.tg-balloon-close {
  position: absolute;
  top: 10px;
  right: 12px;
  background: none;
  border: none;
  font-size: 13px;
  color: #bbb;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 50%;
  line-height: 1;
}
.tg-balloon-close:hover { color: #666; background: #f4f3ff; }
.tg-balloon-icon { font-size: 26px; margin-bottom: 8px; }
.tg-balloon-text { font-size: 13px; color: #444; line-height: 1.5; margin-bottom: 12px; }
.tg-balloon-btn {
  display: block;
  background: #229ED9;
  color: #fff;
  text-align: center;
  padding: 9px 0;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  transition: background 0.15s;
}
.tg-balloon-btn:hover { background: #1a7eaa; }

/* Transition */
.tg-balloon-enter-active, .tg-balloon-leave-active { transition: opacity 0.3s, transform 0.3s; }
.tg-balloon-enter-from, .tg-balloon-leave-to { opacity: 0; transform: translateY(12px); }

@media (max-width: 480px) {
  .tg-widget { bottom: 16px; right: 0; }
  .tg-balloon { width: 200px; }
}
</style>
